import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import _ from 'lodash';
import ListHeader from './components/ListHeader';
import MessagesList from './components/MessagesList';
import UserSearch from './components/UserSearch';
import ConversationHeader from './components/ConversationHeader';
import ConversationArea from './components/ConversationArea';
import MessageArea from './components/MessageArea';
import { Wrapper } from './styles';
import {
  getConversationsAction, getFullConversationAction, getUsersAction, startNewConversationAction,
  setConversationPartnerAction, sendMessageAction, markPostsAsSeenAction,
  setFullConversationAction,
} from './actions';
import TimeTracker from '@shared/TimeTracker';
import { ActivityType } from '@shared/TimeTracker/types';
import { mentorRoles } from '@shared/constants/roles';

export class Messaging extends Component {
  componentDidMount() {
    const {
      conversations,
      getConversations,
      getUsers,
      currentConversation,
      isNewConversationStarted,
      getFullConversation,
      history,
    } = this.props;

    if (_.isEmpty(conversations)) {
      getConversations();
    } else if (_.isEmpty(currentConversation) && !isNewConversationStarted) {
      const indexSelectedUser = history.location.state?.index;
      this.chooseConversation(conversations[indexSelectedUser]);
    } else if (!_.isEmpty(currentConversation) && !isNewConversationStarted) {
      getFullConversation(currentConversation.id);
    }

    getUsers();
  }

  componentDidUpdate(prevProps) {
    const {
      conversations, currentConversation, fetchingFullConversation, isNewConversationStarted,
    } = this.props;
    if (!fetchingFullConversation && _.isEmpty(currentConversation) && !isNewConversationStarted) {
      this.chooseConversation(conversations[0]);
    }
    if (!fetchingFullConversation && prevProps.fetchingFullConversation) {
      this.setPostsAsSeen(currentConversation);
    }
  }

  componentWillUnmount() {
    this.props.setFullConversation({});
  }

  setPostsAsSeen(conversation) {
    if (!conversation || !conversation.Users || !conversation.Thread || !conversation.Thread.Posts) {
      return;
    }
    
    const { markPostsAsSeen } = this.props;
    const selfUser = _.find(conversation.Users, { self: true });
    if (!selfUser) {
      return;
    }
    
    const unseenPostIds = conversation.Thread.Posts
      .filter((post) => (!post.seenBy.includes(selfUser.id)))
      .map((post) => post.id);
    if (unseenPostIds.length > 0) markPostsAsSeen(unseenPostIds);
  }

  chooseConversation = (conversation) => {
    if (!conversation) return;
    const { getFullConversation, setConversationPartner } = this.props;
    getFullConversation(conversation.id);
    setConversationPartner(conversation.Users.find((user) => !user.self));
  };

  handleUserSelect = (conversationPartner) => {
    const { conversations, setConversationPartner } = this.props;
    const prevConversations = conversations.filter(({ Users }) => Users.some((user) => user.id === conversationPartner.id));
    if (prevConversations.length) {
      const Open = prevConversations.find(({ Thread }) => !Thread.closed);
      if (Open) {
        this.chooseConversation(Open);
        return;
      }
    }
    setConversationPartner(conversationPartner);
  };

  handleStartConversation = () => {
    const { isNewConversationStarted, startNewConversation } = this.props;
    if (!isNewConversationStarted) {
      startNewConversation();
    }
  };

  handleMessageSending = (messagePayload) => {
    const {
      sendMessage, isNewConversationStarted,
      conversationPartner: { id }, currentConversation,
    } = this.props;
    const threadId = isNewConversationStarted 
      ? null 
      : currentConversation.Thread.id;
    const conversationId = isNewConversationStarted 
      ? null 
      : currentConversation.id;
    sendMessage(id, threadId, messagePayload, conversationId);
    this.setState({ lastSentMessage: messagePayload?.text || messagePayload?.kind || 'message' });
  };

  render() {
    const {
      conversations, currentConversation, users, isNewConversationStarted, fetchingConversations,
      conversationPartner, setConversationPartner, isClinicalMentor
    } = this.props;
    if (fetchingConversations) {
      return null;
    }

    const renderConversation = () => {
      if (!isNewConversationStarted && _.isEmpty(conversationPartner)) {
        return null;
      }
      if (isNewConversationStarted && _.isEmpty(conversationPartner)) {
        return <UserSearch users={users} selectUser={this.handleUserSelect} />;
      }
      return (
        <ConversationHeader
          currentConversation={currentConversation}
          isNewConversationStarted={isNewConversationStarted}
          selectedUser={conversationPartner}
          removeConversationPartner={() => setConversationPartner({})}
        />
      );
    };

    const conversationExist = conversations.some((conversation) => conversation.Users.some((user) => user.id === conversationPartner.id));

    return (
      <Wrapper>
        {isClinicalMentor &&
          !_.isEmpty(conversationPartner) &&
          (!!currentConversation?.id || (isNewConversationStarted && !conversationExist)) && (
            <TimeTracker
              key={currentConversation?.id || conversationPartner.id}
              activityId={currentConversation?.id || null}
              activityType={ActivityType.CHAT}
              userId={conversationPartner.id}
            />
          )}
        <ListHeader startNewConversation={this.handleStartConversation} />
        <MessagesList
          conversations={conversations}
          currentConversation={currentConversation}
          isNewConversationStarted={isNewConversationStarted}
          chooseConversation={this.chooseConversation}
        />
        {renderConversation()}
        {!isNewConversationStarted && !_.isEmpty(conversationPartner)
          && (
            <ConversationArea
              currentConversation={currentConversation}
              lastSentMessage={this.state?.lastSentMessage}
            />
          )}
        {!_.isEmpty(conversationPartner) && (
        <MessageArea
          currentConversation={currentConversation}
          conversationPartner={conversationPartner}
          sendMessage={this.handleMessageSending}
        />
        )}
      </Wrapper>
    );
  }
}

Messaging.propTypes = {
  conversations: PropTypes.array,
  currentConversation: PropTypes.object,
  users: PropTypes.array,
  isClinicalMentor: PropTypes.bool,
  getConversations: PropTypes.func,
  getFullConversation: PropTypes.func,
  getUsers: PropTypes.func,
  isNewConversationStarted: PropTypes.bool,
  fetchingFullConversation: PropTypes.bool,
  fetchingConversations: PropTypes.bool,
  startNewConversation: PropTypes.func,
  conversationPartner: PropTypes.object,
  setConversationPartner: PropTypes.func,
  sendMessage: PropTypes.func,
  markPostsAsSeen: PropTypes.func,
  setFullConversation: PropTypes.func,
};

export const mapStateToProps = (state) => {
  const {
    conversations, currentConversation, users, isNewConversationStarted,
    fetchingFullConversation, fetchingConversations, conversationPartner,
  } = state.getIn(['messaging']).toJS();
  const { roles = [] } = state.getIn(['app', 'authentication', 'user']).toJS();
  const isClinicalMentor = roles.some((roleId) => mentorRoles.includes(roleId));

  return {
    conversations,
    currentConversation,
    users,
    isClinicalMentor,
    isNewConversationStarted,
    fetchingFullConversation,
    fetchingConversations,
    conversationPartner,
  };
};

export const mapDispatchToProps = (dispatch) => ({
  getConversations: () => dispatch(getConversationsAction()),
  getFullConversation: (id) => dispatch(getFullConversationAction(id)),
  getUsers: () => dispatch(getUsersAction()),
  startNewConversation: () => dispatch(startNewConversationAction()),
  setConversationPartner: (user) => dispatch(setConversationPartnerAction(user)),
  sendMessage: (participantIds, threadId, messagePayload, conversationId) => dispatch(sendMessageAction(participantIds, threadId, messagePayload, conversationId)),
  markPostsAsSeen: (ids) => dispatch(markPostsAsSeenAction(ids)),
  setFullConversation: (conversation) => dispatch(setFullConversationAction(conversation)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  withConnect,
)(Messaging);
